import { useContext, useState } from "react";

import { Container, Grid2Props as GridProps } from "@mui/material";

import { GridRenderCellParams } from "@mui/x-data-grid";
import {
  ActionIcon,
  Button,
  CopyButton,
  Divider,
  Modal,
  PasswordInput,
  Popover,
  Select,
  SelectProps,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAt,
  IconEdit,
  IconEye,
  IconKey,
  IconPhone,
  IconPlus,
  IconReload,
  IconTrash,
  IconUser,
  IconX,
} from "@tabler/icons-react";

import dayjs from "dayjs";

import { AdminRoles, Colors } from "../../Lib/Data";
import { AdminProfileProps } from "../../Lib/Types";

import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultSuccessNotification,
  generateRandomString,
  getEllipsisFromString,
  showResponseErrors,
  showValidationErrors,
  validateEmail,
} from "../../Lib/Methods";
import { PaginatedResponse } from "../../Lib/Responses";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import { AppContext } from "../Dashboard";

import "./styles.scss";

const selectProps: SelectProps = {
  allowDeselect: false,
};
const gridItemProps: GridProps = {
  size: {
    xl: 3,
    lg: 4,
    md: 4,
    sm: 6,
    xs: 12,
  },
};
const Team: React.FC = () => {
  const context = useContext(AppContext);
  const {
    data: team,
    reloadData: reloadTeam,
    isLoading: isLoadingTeam,
  } = usePerformRequest<PaginatedResponse<AdminProfileProps[]>>({
    route: `${Endpoints.GetTeam()}`,
    method: "GET",
  });

  const memberForm = useForm<AdminProfileProps & { password: string }>({
    initialValues: {
      id: "",
      name: "",
      createdAt: dayjs().toString(),
      role: "operations",
      phone: "",
      password: "",
      email: "",
    },
    validate: {
      name: (value) => (value && value.length > 4 ? null : "Please enter name"),
      phone: (value) => (value && value.length === 11 ? null : "Invalid phone"),
      email: (value) => (validateEmail(value) ? null : "Enter a valid email"),
    },
  });

  const [viewingMember, setViewingMember] = useState<AdminProfileProps>();

  const [memberModalAction, setMemberModalAction] = useState<
    "create" | "update" | "view"
  >("create");

  const [
    isMemberModalOpened,
    { open: OpenMemberModal, close: CloseMemberModal },
  ] = useDisclosure(false);

  const [isCreatingOrUpdatingProfile, setCreatingOrUpdatingProfile] =
    useState<boolean>(false);
  const CreateNewMember = async () => {
    const { hasErrors: formHasErrors } = memberForm.validate();
    // @ts-ignore
    const { name, role, phone, email, password } = memberForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetAddTeamMemberProfile(),
        data: {
          name,
          role,
          password,
          email,
          phone,
        },
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if (r.status === 403) {
          showValidationErrors(r);
        }
        if (r.status === 201) {
          CancelCreateMember();
          reloadTeam();
          DefaultSuccessNotification("Plan created successfully!");
        }
      }
    }
  };
  const UpdateMember = async () => {
    const { hasErrors: formHasErrors } = memberForm.validate();
    // @ts-ignore
    const { id, name, role, phone, email, password } = memberForm.values;

    if (!formHasErrors) {
      setCreatingOrUpdatingProfile(true);
      const r = await PerformRequest({
        method: "PATCH",
        route: Endpoints.GetUpdateTeamMemberProfile(id),
        data: {
          name,
          role,
          email,
          phone,
        },
        callback: () => {
          setCreatingOrUpdatingProfile(false);
        },
      });
      setCreatingOrUpdatingProfile(false);
      if (r) {
        if ([403, 409].includes(r.status)) {
          showResponseErrors(r);
        }
        if (r.status === 200) {
          CancelCreateMember();
          reloadTeam();
          DefaultSuccessNotification("Profile has been updated!");
        }
      }
    }
  };

  const [deletingMemberID, setDeletingMemberID] = useState<string>("");
  const DeleteMember = async (id: string) => {
    // @ts-ignore
    setDeletingMemberID(id);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetDeleteTeamMemberProfile(id),

      callback: () => {
        setCreatingOrUpdatingProfile(false);
        setDeletingMemberID("");
      },
    });
    setDeletingMemberID("");
    setCreatingOrUpdatingProfile(false);
    if (r) {
      if (r.status === 200) {
        CancelCreateMember();
        reloadTeam();
        DefaultSuccessNotification("Profile has been REMOVED!");
      }
    }
  };

  const CancelCreateMember = () => {
    memberForm.reset();
    setDeletingMemberID("");
    CloseMemberModal();
  };
  const memberFormInputProps = {
    my: 5,
  };

  return (
    <Container maxWidth="lg">
      <div className="team-container">
        <div className="component-header flex-row align-end justify-between">
          <Text className="label">Team Members</Text>
          <div className="flex-row align-center">
            <Button
              color={Colors.GreenPrimary}
              rightSection={<IconPlus size={16} />}
              mr={10}
              onClick={() => {
                OpenMemberModal();
                setMemberModalAction("create");
                memberForm.reset();
              }}
              disabled={
                isCreatingOrUpdatingProfile ||
                isLoadingTeam ||
                context?.profile?.role !== "admin"
              }
            >
              Add Profile
            </Button>
            <Button
              color={Colors.GreenPrimary}
              variant="outline"
              onClick={reloadTeam}
              loading={isLoadingTeam}
              rightSection={<IconReload size={16} />}
            >
              Reload
            </Button>
          </div>
        </div>
        <br />

        <div className="team">
          {isLoadingTeam ? (
            <MegaLoader
              size={50}
              containerStyle={{
                marginTop: 45,
                marginBottom: 45,
              }}
            />
          ) : (
            <>
              {!team?.data || team?.data.length === 0 ? (
                <DisplayAlert
                  title="No Plans Found"
                  children={<Text>No member profiles except you!</Text>}
                />
              ) : (
                <DatagridDisplay
                  rows={team?.data.filter((t) => t.id !== context?.profile?.id)}
                  isLoading={isLoadingTeam}
                  columns={[
                    {
                      field: "name",
                      flex: 1,
                      headerName: "Name",
                      renderCell: (
                        params: GridRenderCellParams<AdminProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text c={Colors.BlackPrimary} fz={13}>
                            {params.row.name}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      field: "email",
                      flex: 1,
                      headerName: "Email",
                      renderCell: (
                        params: GridRenderCellParams<AdminProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text c={Colors.BlackPrimary} fz={13}>
                            {params.row.email}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      field: "phone",
                      flex: 1,
                      headerName: "Phone",
                      renderCell: (
                        params: GridRenderCellParams<AdminProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text c={Colors.BlackPrimary} fz={13}>
                            {params.row.phone}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      field: "role",
                      flex: 1,
                      headerName: "Role",
                      renderCell: (
                        params: GridRenderCellParams<AdminProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <Text c={Colors.BlackPrimary} tt="capitalize" fz={13}>
                            {params.row.role}
                          </Text>
                        </div>
                      ),
                    },

                    {
                      headerName: "Actions",
                      field: "id",
                      flex: 1,
                      renderCell: (
                        params: GridRenderCellParams<AdminProfileProps>
                      ) => (
                        <div className="flex-row flex-1 align-center">
                          <div className="flex-row align-center">
                            <ActionIcon
                              disabled={context?.profile?.role !== "admin"}
                              variant="subtle"
                              onClick={() => {
                                OpenMemberModal();
                                setMemberModalAction("update");
                                // @ts-ignore
                                memberForm.setValues({
                                  id: params.row.id,
                                  name: params.row.name,
                                  createdAt: params.row.createdAt,
                                  role: params.row.role,
                                  phone: params.row.phone,
                                  email: params.row.email,
                                });
                              }}
                            >
                              <IconEdit size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <ActionIcon
                              disabled={context?.profile?.role !== "admin"}
                              variant="subtle"
                              onClick={() => {
                                OpenMemberModal();
                                setMemberModalAction("view");

                                setViewingMember(params.row);
                              }}
                            >
                              <IconEye size={18} />
                            </ActionIcon>
                            &nbsp; &nbsp;
                            <Popover
                              width={200}
                              position="bottom"
                              withArrow
                              shadow="md"
                            >
                              <Popover.Target>
                                <ActionIcon
                                  disabled={context?.profile?.role !== "admin"}
                                  variant="subtle"
                                  color={Colors.RedPrimary}
                                >
                                  <IconTrash size={18} />
                                </ActionIcon>
                              </Popover.Target>
                              <Popover.Dropdown>
                                <Text size="xs" c={Colors.RedPrimary}>
                                  This action CANNOT be undone!
                                </Text>
                                <Button
                                  color={Colors.RedPrimary}
                                  mt={5}
                                  w="100%"
                                  onClick={() => {
                                    DeleteMember(params.row.id);
                                  }}
                                  loading={deletingMemberID === params.row.id}
                                  disabled={
                                    deletingMemberID.length > 0 &&
                                    deletingMemberID !== params.row.id
                                  }
                                >
                                  Delete
                                </Button>
                              </Popover.Dropdown>
                            </Popover>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </>
          )}
        </div>
      </div>
      <Modal
        opened={isMemberModalOpened}
        onClose={CancelCreateMember}
        centered
        size="lg"
        withCloseButton={false}
      >
        <div className="plan-modal flex-col">
          <div className="flex-row align-center width-100 justify-between">
            <div className="flex-row align-center">
              <Text tt="capitalize" c={Colors.BluePrimary} fw={500} fz={17}>
                {memberModalAction} Profile
              </Text>
            </div>
            <ActionIcon
              variant="outline"
              color={Colors.BluePrimary}
              onClick={CancelCreateMember}
            >
              <IconX color={Colors.BluePrimary} size={18} />
            </ActionIcon>
          </div>
          {memberModalAction === "view" ? (
            <>
              {viewingMember && (
                <>
                  <Table
                    mt={20}
                    variant="vertical"
                    layout="fixed"
                    withTableBorder
                  >
                    <Table.Thead>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th w={200}>Email</Table.Th>
                        <Table.Td>
                          <CopyButton value={viewingMember.email}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <div
                                  className="pointer flex-row align-center"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                    height: "100%",
                                  }}
                                >
                                  {getEllipsisFromString(
                                    viewingMember.email,
                                    20
                                  )}
                                </div>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th w={200}>Phone</Table.Th>
                        <Table.Td>
                          <CopyButton value={viewingMember.phone}>
                            {({ copied, copy }) => (
                              <Tooltip
                                label={copied ? "Copied!" : "Copy to clipboard"}
                              >
                                <div
                                  className="pointer flex-row align-center"
                                  onClick={copy}
                                  style={{
                                    color: copied
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary,
                                    height: "100%",
                                  }}
                                >
                                  {getEllipsisFromString(
                                    viewingMember.phone,
                                    14
                                  )}
                                </div>
                              </Tooltip>
                            )}
                          </CopyButton>
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Name</Table.Th>
                        <Table.Td>{viewingMember.name}</Table.Td>
                      </Table.Tr>
                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Role</Table.Th>
                        <Table.Td tt="capitalize">
                          {viewingMember.role}
                        </Table.Td>
                      </Table.Tr>

                      <Table.Tr key={generateRandomString(12)}>
                        <Table.Th>Creation Date</Table.Th>
                        <Table.Td>
                          {dayjs(viewingMember.createdAt).format(
                            "ddd, MMMM DD YYYY"
                          )}
                        </Table.Td>
                      </Table.Tr>
                    </Table.Thead>
                  </Table>
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex-row align-center justify-between width-100">
                <TextInput
                  {...memberForm.getInputProps("email")}
                  {...memberFormInputProps}
                  label="Email"
                  placeholder="Enter email"
                  spellCheck={false}
                  w="48%"
                  leftSection={<IconAt size={16} />}
                />
                <TextInput
                  {...memberForm.getInputProps("phone")}
                  {...memberFormInputProps}
                  w="48%"
                  label="Phone"
                  placeholder="0802******"
                  spellCheck={false}
                  leftSection={<IconPhone size={16} />}
                />
              </div>
              <TextInput
                {...memberForm.getInputProps("name")}
                {...memberFormInputProps}
                w="100%"
                label="Name"
                placeholder="Full Name"
                spellCheck={false}
                leftSection={<IconUser size={16} />}
              />

              <Select
                leftSection={<IconKey size={16} />}
                label="Role"
                {...selectProps}
                {...memberForm.getInputProps("role")}
                {...memberFormInputProps}
                w="100%"
                styles={{
                  option: { textTransform: "capitalize" },
                  input: { textTransform: "capitalize" },
                }}
                data={AdminRoles.map((role) => {
                  return {
                    value: role,
                    label: role,
                  };
                })}
              />

              {memberModalAction === "create" && (
                <>
                  <Divider w="100%" my={10} />
                  <PasswordInput
                    {...memberForm.getInputProps("password")}
                    {...memberFormInputProps}
                    label="Password"
                    placeholder="Enter login password"
                    spellCheck={false}
                    leftSection={<IconKey size={16} />}
                  />
                </>
              )}
              <div className="flex-row width-100 justify-between align-center">
                <Button
                  mt={10}
                  w="48%"
                  color={Colors.GreenPrimary}
                  onClick={
                    memberModalAction === "create"
                      ? CreateNewMember
                      : UpdateMember
                  }
                  loading={isCreatingOrUpdatingProfile}
                  tt="capitalize"
                >
                  {memberModalAction}
                </Button>
                <Button
                  mt={10}
                  w="48%"
                  variant="outline"
                  onClick={CancelCreateMember}
                  disabled={isCreatingOrUpdatingProfile}
                  color={Colors.RedPrimary}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </Container>
  );
};

export default Team;
